import { ReactNode } from 'react';

// third-party
import { IntlProvider } from 'react-intl';

// project import
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { store } from 'store';
import { APIUserSlice } from 'store/api/user';
import { NEW_DESIGN_LOCALE, newDesignTranslations, translations } from 'utils/getTranslations';
import { isNewDesignNow, onKeptDesignChange } from 'utils/impersonationDesign';
import { setNewDesignCopy } from 'utils/newDesignCopy';

// ==============================|| LOCALIZATION ||============================== //

const selectCurrentUser = APIUserSlice.endpoints.getUserData.select();

// The catalog outside React follows the design in force — the cached user's, or the one chosen in this browser while
// impersonating — as the store or that choice changes, so it is settled before the render the change causes: a
// component's `t()` then reads the same wording as the provider below hands it. The cast: the store's own state
// type does not carry the tags this slice adds.
const followDesign = () => setNewDesignCopy(isNewDesignNow(selectCurrentUser(store.getState() as never).data));
store.subscribe(followDesign);
onKeptDesignChange(followDesign);

interface Props {
    children: ReactNode;
}

/**
 * The app's messages and locale: `en.json` in `eng`, with the new design's wording over it, in that design's locale,
 * while it is on. The default locale follows, so a message falling back to its default is not reported as missing.
 */
const Locales = ({ children }: Props): JSX.Element => {
    const isNewDesign = useIsNewDesign();
    const locale = isNewDesign ? NEW_DESIGN_LOCALE : 'eng';

    return (
        <IntlProvider
            locale={locale}
            defaultLocale={locale}
            messages={isNewDesign ? newDesignTranslations : translations}
        >
            {children}
        </IntlProvider>
    );
};

export default Locales;
